import { useCallback } from 'react';

import { getContext } from '@/context';
import { getAppStore, setAppStore, useAppStore } from '@/store';
import useAuth from '@/hooks/useAuth';
import useDownloadFlow from '@/hooks/useDownloadFlow';
import useShareFlow from '@/hooks/useShareFlow';
import deleteDriveFiles from '@/requests/cloud-storage/deleteDriveFiles';
import setFileReaction from '@/requests/cloud-storage/setFileReaction';
import {
  addMediaToEditor,
  openMediaInEditor,
} from '@/utils/editor/addMediaToEditor';
import { closeMiniapp } from '@/utils/editor/closeAndNavigate';
import closeChooser from '@/utils/editor/closeChooser';
import sendAiGenScreenActionAnalytics from '@/utils/analytics/sendAiGenScreenActionAnalytics';
import getCreditsBalance from '@/utils/analytics/getCreditsBalance';
import trackPresetCredits from '@/utils/analytics/trackPresetCredits';
import sendPhotoEditorOpenAnalytics from '@/utils/analytics/sendPhotoEditorOpenAnalytics';
import getAnalyticsSettingsFromAttributes from '@/utils/analytics/getAnalyticsSettingsFromAttributes';
import sendContentTryAnalytics from '@/utils/analytics/sendContentTryAnalytics';
import parsePresetAttributes from '@/utils/file/parsePresetAttributes';
import { isGenaiRoute } from '@/utils/environment/surface';
import { AiGenScreenAction, ScreenType } from '@/types/analytics';
import { isDesktop } from '@/consts/device';
import { ErrorMessages } from '@/consts/errors';
import { ToastType } from '@/store/toast/types';

import type { IAppStore } from '@/store';
import type { ContentFile, UserReaction } from '@/types';
import { MediaType, PreviewMediaType, UserReactions } from '@/types';
import { MINIAPP_PACKAGE_IDS } from '@/consts/configs';
import { RightPanelTab } from '@/store/navigation';
import useLibraryItemInfoMutation from './queries/useLibraryItemInfoMutation';

// External selector for stable reference
const selector = (store: IAppStore) => ({
  removeContentFiles: store.removeContentFiles,
  updateContentFile: store.updateContentFile,
  updateLightboxItem: store.updateLightboxItem,
  showToast: store.showToast,
});

const useFileActions = () => {
  const { isAuthorized } = useAuth();
  const {
    removeContentFiles,
    updateContentFile,
    updateLightboxItem,
    showToast,
  } = useAppStore(selector);
  const presetMutation = useLibraryItemInfoMutation();

  const { handleDownload: downloadFlow } = useDownloadFlow({
    screenType: ScreenType.HISTORY,
  });

  const { handleShare: shareFlow } = useShareFlow({
    screenType: ScreenType.HISTORY,
  });

  const handleDownload = useCallback(
    async (file: ContentFile) => {
      await downloadFlow(
        file,
        getAnalyticsSettingsFromAttributes(file.attributes)
      );
    },
    [downloadFlow]
  );

  const handleReport = useCallback(async (file: ContentFile) => {
    const sourceUrl = file?.url;
    if (!sourceUrl) return;
    sendAiGenScreenActionAnalytics({
      action: AiGenScreenAction.REPORT,
      screen_type: ScreenType.PREVIEW,
      settings: getAnalyticsSettingsFromAttributes(file.attributes),
    });
    try {
      const context = getContext();
      const state = context.general.sharedStorage.getState();
      await context.general.sharedStorage.dispatch({
        ...state,
        reportingParams: {
          reportingType: 'aigenerated',
          sourceUrl,
        },
      });
      await context.handlers.redirectToPackageId(
        MINIAPP_PACKAGE_IDS.REPORTING,
        { comeback: true }
      );
    } catch {
      // Reporting redirect failed silently
    }
  }, []);

  const handleShare = useCallback(
    async (file: ContentFile) => {
      await shareFlow(
        {
          url: file.url,
          driveUid: file.uuid,
        },
        getAnalyticsSettingsFromAttributes(file.attributes)
      );
    },
    [shareFlow]
  );

  const handleConfirmDelete = useCallback(
    async (file: ContentFile | null, screenType = ScreenType.PREVIEW) => {
      if (!file) return;

      sendAiGenScreenActionAnalytics({
        action: AiGenScreenAction.DELETE,
        screen_type: screenType,
      });

      removeContentFiles([file.uuid]);
      if (isAuthorized) {
        await deleteDriveFiles([file.uuid]);
      }
      showToast('File deleted');
    },
    [removeContentFiles, isAuthorized, showToast]
  );

  const handleOpenInEditor = useCallback(async (file: ContentFile) => {
    if (file.type === MediaType.AUDIO) return;

    sendAiGenScreenActionAnalytics({
      action: AiGenScreenAction.EDIT,
      screen_type: ScreenType.HISTORY,
    });
    if (isDesktop) {
      sendPhotoEditorOpenAnalytics();
    }

    const mediaType =
      file.type === MediaType.VIDEO
        ? PreviewMediaType.VIDEO
        : PreviewMediaType.IMAGE;

    if (isGenaiRoute()) {
      await openMediaInEditor({ mediaUrl: file.url, mediaType });
    } else {
      await addMediaToEditor({ mediaUrl: file.url, mediaType });
      sendContentTryAnalytics();
      await closeMiniapp();
      closeChooser();
    }
  }, []);

  const handleReuseTemplate = useCallback(
    async (file: ContentFile, slug: string) => {
      sendAiGenScreenActionAnalytics({
        action: AiGenScreenAction.REUSE_TEMPLATE,
        screen_type: ScreenType.PREVIEW,
        settings: {
          ...getAnalyticsSettingsFromAttributes(file.attributes),
          credits_balance: getCreditsBalance(),
        },
      });
      const { category } = parsePresetAttributes(file.attributes?.category);
      trackPresetCredits({ slug, category: category ?? '' });
      setAppStore({
        rightPanelTab: RightPanelTab.Presets,
        presetFeedEntrySlug: slug,
        ...(category && { activeTabSlug: category }),
      });
      try {
        await presetMutation.mutateAsync(slug);
        const { selectedPreset } = getAppStore();
        if (selectedPreset?.category) {
          setAppStore({ activeTabSlug: selectedPreset.category });
        }
      } catch {
        // Mutation's onError already restores screen.
      }
    },
    [presetMutation]
  );

  const handleReact = useCallback(
    async (
      file: ContentFile,
      reaction: UserReaction,
      screenType = ScreenType.PREVIEW
    ) => {
      const current = file.attributes?.userReaction;
      const next = current === reaction ? null : reaction;

      sendAiGenScreenActionAnalytics({
        action:
          reaction === UserReactions.Like
            ? AiGenScreenAction.LIKE
            : AiGenScreenAction.DISLIKE,
        screen_type: screenType,
        settings: getAnalyticsSettingsFromAttributes(file.attributes),
      });

      // The open lightbox holds a snapshot, so it is patched alongside the list.
      const optimistic = {
        uuid: file.uuid,
        updates: {
          attributes: { ...file.attributes, userReaction: next ?? undefined },
        },
      };
      updateContentFile(optimistic);
      updateLightboxItem(optimistic);

      const isReactionSaved = await setFileReaction({
        id: file.uuid,
        reaction: next,
      });
      if (!isReactionSaved) {
        const reverted = {
          uuid: file.uuid,
          updates: {
            attributes: { ...file.attributes, userReaction: current },
          },
        };
        updateContentFile(reverted);
        updateLightboxItem(reverted);
        showToast(ErrorMessages.saveReaction, ToastType.Error);
      }
    },
    [updateContentFile, updateLightboxItem, showToast]
  );

  return {
    handleDownload,
    handleShare,
    handleConfirmDelete,
    handleOpenInEditor,
    handleReport,
    handleReuseTemplate,
    handleReact,
  };
};

export default useFileActions;
